import { useEffect, useState } from 'react';

/**
 * The warning tone starts this many minutes before the target (tasks/cleaning-timer.md D8) — the same
 * moment the cleaner's phone gets its one push. Mirrors `CLEAN_TARGET_NUDGE_MINUTES` in
 * be/config/tasks.js; change both together.
 */
const WARNING_MINUTES = 10;

/**
 * The cleaning target timer's state, re-read every second.
 *
 * The server decides whether a clean is timed and records the target when it starts
 * (`task.cleanTargetMinutes`); this hook only counts (D6). Elapsed time runs from the
 * server-stamped start, shifted by `clockOffsetMs` — the server's clock minus the phone's — so a
 * phone set a few minutes wrong still shows the right countdown. Computed from the clock on every
 * tick rather than decremented, so a phone that sleeps or a tab put in the background comes back
 * showing the true figure.
 *
 * @returns {null | { state: 'onTrack'|'warning'|'over', seconds: number, targetMinutes: number }}
 *   `seconds` is the time left, or — once `over` — the time past the target.
 */
export default function useCleanTargetTimer(startedAt, targetMinutes, clockOffsetMs = 0) {
  const active = Boolean(startedAt) && targetMinutes > 0;
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (!active) return undefined;
    setNow(Date.now());
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [active]);

  if (!active) return null;

  const started = new Date(startedAt).getTime();
  if (Number.isNaN(started)) return null;

  const remainingMs = targetMinutes * 60000 - (now + clockOffsetMs - started);
  if (remainingMs <= 0) {
    return { state: 'over', seconds: Math.floor(-remainingMs / 1000), targetMinutes };
  }
  return {
    state: remainingMs <= WARNING_MINUTES * 60000 ? 'warning' : 'onTrack',
    seconds: Math.ceil(remainingMs / 1000),
    targetMinutes
  };
}
